import { original } from 'immer';
import lodashCloneDeep from 'lodash/cloneDeep';
import { setPathLength } from 'nmr-correlation';
import { Filters, FiltersManager } from 'nmr-processing';
import { DATUM_KIND, SIGNAL_INLCUDED_KINDS, } from '../../../data/constants/signalsKinds';
import { changeZoneSignal, detectZones, detectZonesManual, isSpectrum2D, } from '../../../data/data2d/Spectrum2D';
import { unlink, unlinkInAssignmentData, } from '../../../data/utilities/ZoneUtilities';
import { isNumber } from '../../../data/utilities/isNumber';
import { defaultZonesViewState } from '../../hooks/useActiveSpectrumZonesViewState';
import get2DRange from '../helper/get2DRange';
import { getActiveSpectrum } from '../helper/getActiveSpectrum';
import { handleUpdateCorrelations } from './CorrelationsActions';
import { setDomain } from './DomainActions';
//action
function handleChangeZonesFactor(draft, action) {
    draft.toolOptions.data.zonesNoiseFactor = action.payload.zonesNoiseFactor;
    draft.toolOptions.data.zonesMinMaxRatio = action.payload.zonesMinMaxRatio;
}
//action
function handleAdd2dZone(draft, action) {
    const activeSpectrum = getActiveSpectrum(draft);
    if (activeSpectrum?.id) {
        const { index } = activeSpectrum;
        const drawnZone = get2DRange(draft, action.payload);
        const datum = draft.data[index];
        const zones = detectZonesManual(original(datum), {
            selectedZone: drawnZone,
            thresholdFactor: draft.toolOptions.data.zonesNoiseFactor,
            maxPercentCutOff: draft.toolOptions.data.zonesMinMaxRatio,
            convolutionByFFT: false,
        });
        datum.zones.values = datum.zones.values.concat(zones);
        handleUpdateCorrelations(draft);
    }
}
//action
function handleAutoZonesDetection(draft, action) {
    const activeSpectrum = getActiveSpectrum(draft);
    if (activeSpectrum?.id) {
        const { index } = activeSpectrum;
        const { zonesNoiseFactor: thresholdFactor, zonesMinMaxRatio: maxPercentCutOff, } = action.payload;
        const [fromX, toX] = draft.xDomain;
        const [fromY, toY] = draft.yDomain;
        const detectionOptions = {
            selectedZone: { fromX, toX, fromY, toY },
            thresholdFactor,
            maxPercentCutOff,
        };
        const datum = draft.data[index];
        const zones = detectZones(original(datum), detectionOptions);
        datum.zones.values = datum.zones.values.concat(zones);
        handleUpdateCorrelations(draft);
    }
}
//action
function handleAutoSpectraZonesDetection(draft) {
    for (const datum of draft.data) {
        const { info, data } = datum;
        if (info.dimension === 2 && info.isFt) {
            const { minX, maxX, minY, maxY } = data.rr;
            const detectionOptions = {
                selectedZone: { fromX: minX, toX: maxX, fromY: minY, toY: maxY },
                thresholdFactor: 1,
                maxPercentCutOff: 0.03,
            };
            const zones = detectZones(original(datum), detectionOptions);
            datum.zones.values = datum.zones.values.concat(zones);
            handleUpdateCorrelations(draft);
        }
    }
}
//action
function handleChangeZoneSignalDelta(draft, action) {
    const { zoneId, signal } = action.payload;
    const activeSpectrum = getActiveSpectrum(draft);
    if (activeSpectrum?.id) {
        const { index } = activeSpectrum;
        const { xShift, yShift } = changeZoneSignal(draft.data[index], zoneId, signal);
        const filters = [];
        if (xShift !== 0) {
            filters.push({ name: Filters.shift2DX.id, value: { shift: xShift } });
        }
        if (yShift !== 0) {
            filters.push({ name: Filters.shift2DY.id, value: { shift: yShift } });
        }
        FiltersManager.applyFilter(draft.data[index], filters);
        setDomain(draft);
        handleUpdateCorrelations(draft);
    }
}
function getZoneIndex(state, spectrumIndex, zoneId) {
    return state.data[spectrumIndex].zones.values.findIndex((zone) => zone.id === zoneId);
}
//action
function handleChangeZoneSignalKind(draft, action) {
    const state = original(draft);
    const activeSpectrum = getActiveSpectrum(state);
    if (activeSpectrum?.id) {
        const { index } = activeSpectrum;
        const { zoneData, kind } = action.payload;
        const zoneIndex = getZoneIndex(state, index, zoneData.id);
        const _zone = draft.data[index].zones.values[zoneIndex];
        _zone.signals[zoneData.tableMetaInfo.signalIndex].kind = kind;
        _zone.kind = SIGNAL_INLCUDED_KINDS.includes(kind)
            ? DATUM_KIND.signal
            : DATUM_KIND.mixed;
        handleUpdateCorrelations(draft);
    }
}
//action
function handleDeleteZone(draft, action) {
    const state = original(draft);
    const activeSpectrum = getActiveSpectrum(state);
    if (activeSpectrum?.id) {
        const { index } = activeSpectrum;
        const { id, assignmentData } = action.payload;
        if (id) {
            const zone = draft.data[index].zones.values.find((zone) => zone.id === id);
            unlinkInAssignmentData(assignmentData, [zone || {}]);
            const zoneIndex = getZoneIndex(state, index, id);
            draft.data[index].zones.values.splice(zoneIndex, 1);
        }
        else {
            unlinkInAssignmentData(assignmentData, draft.data[index].zones.values);
            draft.data[index].zones.values = [];
        }
        handleUpdateCorrelations(draft);
    }
}
function deleteSignal2D(draft, options) {
    const { spectrum, zone, signal, assignmentData } = options;
    if (spectrum && zone) {
        const datum2D = draft.data.find((datum) => datum.id === spectrum.id);
        const zoneIndex = datum2D.zones.values.findIndex((_zone) => _zone.id === zone.id);
        const signalIndex = zone.signals.findIndex((_signal) => _signal.id === signal.id);
        // remove assignments for the signal in zone object and global state
        const _zone = unlink(lodashCloneDeep(zone), false, signalIndex, undefined);
        unlinkInAssignmentData(assignmentData, [{ signals: [signal] }], undefined);
        _zone.signals.splice(signalIndex, 1);
        datum2D.zones.values[zoneIndex] = _zone;
        // if no signals are existing in a zone anymore then delete this zone
        if (_zone.signals.length === 0) {
            unlinkInAssignmentData(assignmentData, [_zone]);
            datum2D.zones.values.splice(zoneIndex, 1);
        }
        handleUpdateCorrelations(draft);
    }
}
//action
function handleDeleteSignal(draft, action) {
    deleteSignal2D(draft, action.payload);
}
//action
function handleSetSignalPathLength(draft, action) {
    const { spectrum, zone, signal, pathLength } = action.payload;
    if (spectrum && zone && signal) {
        const datum2D = draft.data.find((datum) => datum.id === spectrum.id);
        const zoneIndex = datum2D.zones.values.findIndex((_zone) => _zone.id === zone.id);
        const signalIndex = zone.signals.findIndex((_signal) => _signal.id === signal.id);
        const _zone = unlink(lodashCloneDeep(zone), false, signalIndex, undefined);
        _zone.signals[signalIndex].j = {
            ..._zone.signals[signalIndex].j,
            pathLength,
        };
        datum2D.zones.values[zoneIndex] = _zone;
        handleUpdateCorrelations(draft);
    }
}
//action
function unlinkZone(draft, props) {
    const state = original(draft);
    const activeSpectrum = getActiveSpectrum(state);
    if (activeSpectrum?.id) {
        const { index } = activeSpectrum;
        const { zone: zoneData, assignmentData, isOnZoneLevel, signalIndex = -1, axis, } = props;
        if (zoneData) {
            // remove assignments in global state
            const zoneIndex = getZoneIndex(state, index, zoneData.id);
            const zone = lodashCloneDeep(draft.data[index].zones.values[zoneIndex]);
            const _zoneData = unlink(zone, isOnZoneLevel, signalIndex, axis);
            unlinkInAssignmentData(assignmentData, [{ id: zoneData.signals[signalIndex].id }], axis);
            draft.data[index].zones.values[zoneIndex] = _zoneData;
        }
        else {
            const zones = draft.data[index].zones.values.map((zone) => {
                return unlink(zone);
            });
            draft.data[index].zones.values = zones;
            unlinkInAssignmentData(assignmentData, zones);
        }
    }
}
//action
function handleUnlinkZone(draft, action) {
    unlinkZone(draft, action.payload);
}
//action
function handleSetDiaIDZone(draft, action) {
    const state = original(draft);
    const activeSpectrum = getActiveSpectrum(state);
    const { zone: zoneData, diaIDs, axis, signalIndex, nbAtoms } = action.payload;
    if (activeSpectrum?.id && axis) {
        const { index } = activeSpectrum;
        const getNbAtoms = (input, current = 0) => input + current;
        const zoneIndex = getZoneIndex(state, index, zoneData.id);
        const zone = draft.data[index].zones.values[zoneIndex];
        if (!isNumber(signalIndex)) {
            const zoneByAxis = zone[axis];
            zoneByAxis.diaIDs = diaIDs;
            zoneByAxis.nbAtoms = getNbAtoms(nbAtoms, zoneByAxis.nbAtoms);
        }
        else {
            const signalByAxis = zone.signals[signalIndex][axis];
            signalByAxis.diaIDs = diaIDs;
            signalByAxis.nbAtoms = getNbAtoms(nbAtoms, signalByAxis.nbAtoms);
        }
    }
}
//action
function handleSaveEditedZone(draft, action) {
    const state = original(draft);
    const activeSpectrum = getActiveSpectrum(state);
    if (activeSpectrum?.id) {
        const { index } = activeSpectrum;
        const { zone: { tableMetaInfo, ...editedZone }, } = action.payload;
        const zoneIndex = getZoneIndex(state, index, editedZone.id);
        draft.data[index].zones.values[zoneIndex] = editedZone;
        if (editedZone.signals) {
            for (const signal of editedZone.signals) {
                setPathLength(draft.correlations.values, signal.id, signal.j?.pathLength);
            }
        }
        handleUpdateCorrelations(draft);
    }
}
function initializeZoneViewObject(draft, spectrumID) {
    const zonesView = draft.view.zones;
    if (spectrumID in zonesView)
        return;
    const defaultZonesView = { ...defaultZonesViewState };
    zonesView[spectrumID] = defaultZonesView;
}
function togglePeaksViewProperty(draft, key) {
    const activeSpectrum = getActiveSpectrum(draft);
    if (!activeSpectrum?.id)
        return;
    initializeZoneViewObject(draft, activeSpectrum.id);
    const zonesView = draft.view.zones;
    zonesView[activeSpectrum.id][key] = !zonesView[activeSpectrum.id][key];
}
//action
function handleToggleZonesViewProperty(draft, action) {
    const { key } = action.payload;
    togglePeaksViewProperty(draft, key);
}
function handleChangeZoneAssignmentLabel(draft, action) {
    const { zoneID, value } = action.payload;
    const activeSpectrum = getActiveSpectrum(draft);
    if (!activeSpectrum?.id)
        return;
    const { index } = activeSpectrum;
    const spectrum = draft.data[index];
    if (!isSpectrum2D(spectrum))
        return;
    initializeZoneViewObject(draft, activeSpectrum.id);
    const zoneView = draft.view.zones[spectrum.id];
    if (!zoneView.showAssignmentsLabels) {
        zoneView.showAssignmentsLabels = true;
    }
    const zone = spectrum.zones.values.find((zone) => zone.id === zoneID);
    if (zone) {
        zone.assignment = value;
        if (!value) {
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete zoneView.assignmentsLabelsCoordinates[zoneID];
        }
    }
}
function handleSetZoneAssignmentLabelCoordination(draft, action) {
    const { zoneID, coordination } = action.payload;
    const activeSpectrum = getActiveSpectrum(draft);
    if (!activeSpectrum)
        return;
    initializeZoneViewObject(draft, activeSpectrum.id);
    const zonesView = draft.view.zones;
    zonesView[activeSpectrum.id].assignmentsLabelsCoordinates[zoneID] =
        coordination;
}
export { handleAdd2dZone, handleAutoZonesDetection, handleDeleteSignal, deleteSignal2D, handleDeleteZone, handleChangeZoneSignalDelta, handleChangeZoneSignalKind, handleUnlinkZone, unlinkZone, handleSaveEditedZone, handleSetDiaIDZone, handleSetSignalPathLength, handleChangeZonesFactor, handleAutoSpectraZonesDetection, handleToggleZonesViewProperty, handleChangeZoneAssignmentLabel, handleSetZoneAssignmentLabelCoordination, };
